import { CommonResponse } from '@xver/@types/api';
import { User } from '@xver/@types/user';
import httpClient from '@xver/libs/axios';

import {
    ChangePasswordRequestModel,
    ConfirmResetPasswordRequestModel,
    RequestResetPasswordRequestModel,
    RequestResetPasswordResponseModel,
    ResetEmailAddressRequestModel,
    ResetEmailAddressResponseModel,
    OAuthCallbackResponseModel,
    OAuthLinkVerifyRequestModel,
    OAuthLinkVerifyResponseModel,
    Send2FACodeRequestModel,
    Send2FACodeResponseModel,
    SendVerificationCodeResponseModel,
    SignInRequestModel,
    SignInResponseModel,
    SignUpRequestModel,
    SignUpResponseModel,
    Toggle2FARequestModel,
    Toggle2FAResponseModel,
    Verify2FARequestModel,
    Verify2FAResponseModel,
    VerifyChangePasswordRequestModel,
    VerifyChangePasswordResponseModel,
} from './model';

const ENDPOINTS = {
    GET_USER: '/v1/auth',
    REQUEST_CHANGE_PASSWORD: (token: string) => `/v1/auth/request-change-password?token=${token}`,
    CHANGE_PASSWORD: (otp: string) => `/v1/auth/change-password?otp=${otp}`,
    SEND_VERIFY_EMAIL: (token: string) => `/v1/auth/send-verify-email?token=${token}`,
    VERIFY_EMAIL: (ref: string, token: string) => `/v1/auth/verify-email?ref=${ref}&token=${token}`,
    REQUEST_RESET_EMAIL: (token: string) => `/v1/auth/request-reset-email-address?token=${token}`,
    RESET_EMAIL: (otp: string, token: string) =>
        `/v1/auth/reset-email-address?otp=${otp}&token=${token}`,
    SEND_VERIFY_PHONE: (token: string) => `/v1/auth/send-verify-phone?token=${token}`,
    VERIFY_PHONE: (token: string) => `/v1/auth/verify-phone?token=${token}`,
    TOGGLE_2FA: '/v1/auth/toggle-2fa',
    SIGN_IN: '/v1/auth/sign-in',
    SIGN_UP: '/v1/auth/sign-up',
    REQUEST_RESET_PASSWORD: '/v1/auth/reset-password',
    CONFIRM_RESET_PASSWORD: (userID: number) => `/v1/auth/reset-password/${userID}`,
    VERIFY_2FA: '/v1/auth/verify-2fa',
    SEND_2FA_CODE: '/v1/auth/send-2fa-code',
    GITHUB_LOGIN: '/v1/auth/github/login',
    GITHUB_CALLBACK: '/v1/auth/github/callback',
    GITHUB_LINK: '/v1/auth/github/link',
    GITHUB_UNLINK: '/v1/auth/github/unlink',
    OAUTH_LINK_VERIFY: '/v1/auth/oauth/link/verify',
};

export function getUserInfo(): Promise<User> {
    return httpClient.get(ENDPOINTS.GET_USER);
}

export function requestChangePassword(
    token: string,
    payload: VerifyChangePasswordRequestModel
): Promise<VerifyChangePasswordResponseModel> {
    return httpClient.post(ENDPOINTS.REQUEST_CHANGE_PASSWORD(token), payload);
}

export function changePassword(
    otp: string,
    payload: ChangePasswordRequestModel
): Promise<CommonResponse> {
    return httpClient.post(ENDPOINTS.CHANGE_PASSWORD(otp), payload);
}

export function sendEmailVerification(token: string): Promise<{ message: string }> {
    return httpClient.post(ENDPOINTS.SEND_VERIFY_EMAIL(token));
}

export function verifyEmail(ref: string, token: string): Promise<{ token: string }> {
    return httpClient.post(ENDPOINTS.VERIFY_EMAIL(ref, token));
}

export function requestResetEmail(
    token: string,
    payload: ResetEmailAddressRequestModel
): Promise<SendVerificationCodeResponseModel> {
    return httpClient.post(ENDPOINTS.REQUEST_RESET_EMAIL(token), payload);
}

export function resetEmail(otp: string, token: string): Promise<ResetEmailAddressResponseModel> {
    return httpClient.post(ENDPOINTS.RESET_EMAIL(otp, token), {});
}

export function sendPhoneVerification(token: string): Promise<SendVerificationCodeResponseModel> {
    return httpClient.post(ENDPOINTS.SEND_VERIFY_PHONE(token));
}

export function verifyPhone(
    token: string,
    payload: { otpRef: string; otpCode: string }
): Promise<{ token: string }> {
    return httpClient.post(ENDPOINTS.VERIFY_PHONE(token), payload);
}

export function toggle2FA(payload: Toggle2FARequestModel): Promise<Toggle2FAResponseModel> {
    return httpClient.post(ENDPOINTS.TOGGLE_2FA, payload);
}

export function signIn(payload: SignInRequestModel): Promise<SignInResponseModel> {
    const config = payload.turnstileToken
        ? { headers: { 'X-Turnstile-Token': payload.turnstileToken } }
        : {};
    return httpClient.post(ENDPOINTS.SIGN_IN, payload, config);
}

export function signUp(payload: SignUpRequestModel): Promise<SignUpResponseModel> {
    const config = payload.turnstileToken
        ? { headers: { 'X-Turnstile-Token': payload.turnstileToken } }
        : {};
    return httpClient.post(ENDPOINTS.SIGN_UP, payload, config);
}

export function requestResetPassword(
    payload: RequestResetPasswordRequestModel
): Promise<RequestResetPasswordResponseModel> {
    return httpClient.post(ENDPOINTS.REQUEST_RESET_PASSWORD, payload);
}

export function confirmResetPassword(
    userID: number,
    payload: ConfirmResetPasswordRequestModel
): Promise<CommonResponse> {
    return httpClient.post(ENDPOINTS.CONFIRM_RESET_PASSWORD(userID), payload);
}

export function verify2FA(payload: Verify2FARequestModel): Promise<Verify2FAResponseModel> {
    return httpClient.post(ENDPOINTS.VERIFY_2FA, payload);
}

export function send2FACode(payload: Send2FACodeRequestModel): Promise<Send2FACodeResponseModel> {
    return httpClient.post(ENDPOINTS.SEND_2FA_CODE, payload);
}

export function getGithubAuthorizeUrl(): Promise<{ data: { authorizeUrl: string } }> {
    return httpClient.get(ENDPOINTS.GITHUB_LOGIN);
}

export function githubCallback(code: string, state: string): Promise<OAuthCallbackResponseModel> {
    return httpClient.post(ENDPOINTS.GITHUB_CALLBACK, { code, state });
}

// Connect GitHub to the currently logged-in account (authenticated).
export function githubLink(code: string, state: string): Promise<{ message: string }> {
    return httpClient.post(ENDPOINTS.GITHUB_LINK, { code, state });
}

// Disconnect GitHub from the currently logged-in account and revoke the OAuth
// grant (authenticated).
export function githubUnlink(): Promise<{ message: string; data: { revoked: boolean } }> {
    return httpClient.post(ENDPOINTS.GITHUB_UNLINK);
}

export function oauthLinkVerify(
    payload: OAuthLinkVerifyRequestModel
): Promise<OAuthLinkVerifyResponseModel> {
    return httpClient.post(ENDPOINTS.OAUTH_LINK_VERIFY, payload);
}
